import React, { useState } from 'react';
import { Button } from '@ipc/ipc-components';
import { PropTypes } from 'prop-types';

import { TopButtonsWrapper, StyledLoader } from '../../../styles';
import {
  ButtonsList, Download, Table,
} from '../../../elements';
import { redButton } from '../../../styles/themes';
import AlaSlider from '../AlaSlider';
import AlaTableView from '../AlaTableView';

import {
  FILE_NAME,
  TITLE,
  TABLE_COLUMNS,
  TABLE_COLUMNS_EXTENDED,
  PASS_COLUMNS,
  PASS_FILE_NAME,
  PASS_TITLE,
  RECEPTACLE_ID_HEADER,
  DESPATCH_ID_HEADER,
  CONSIGNMENT_ID_HEADER,
  FILE_FORMAT_EXCEL,
  PASS_BILLING_DATA_REPORT,
  FILE_FORMAT_CSV, FILE_FORMAT_PDF, PASS_RECONCILIATION_DATA_REPORT,
} from './constants';
import { useReceptaclesList } from './hooks';
import UpdateStatusModal from './components/UpdateStatusModal';
import FailureReasonModal from './components/FailureReasonModal';
import {
  hasAccess, ROLE_ADMIN, ROLE_CARRIER, ROLE_OPERATOR,
} from '../../../utils/access';
import {
  DOWNLOAD_CARRIER_COLUMN, DOWNLOAD_COLUMNS, DOWNLOAD_OPERATOR_COLUMN,
  TABLE_CARRIER_COLUMN,
  TABLE_OPERATOR_COLUMN,
} from '../../../pages/PreSettlementList/constants';
import request, { POST } from '../../../utils/request';
import UpdateInvoiceNumberModal from './components/UpdateInvoiceNumberModal';
import DeleteInvoiceModal from './components/DeleteInvoiceModal';
import ActionHistoryModal from './components/ActionHistoryModal';

function RecordsListTable({
  recordId, filter = '', search = '', newWarning,
}) {
  const tableColumns = [...TABLE_COLUMNS];
  const downloadColumns = [...DOWNLOAD_COLUMNS];
  const [data, loading, bulkUpdateStatus, bulkInvoiceNumberUpdate, deleteReceptaclesInvoiceNumber] = useReceptaclesList(recordId, search);
  const [fromTo, updateFromTo] = useState([1, 10]);
  const [extended, updateExtended] = useState(false);
  const [selected, updateSelected] = useState([]);
  const [failure, updateFailure] = useState('');
  const [statusModal, updateStatusModal] = useState({});
  const [invoiceNumberModal, updateInvoiceNumberModal] = useState({});
  const [deleteInvoiceModal, updateDeleteInvoiceModal] = useState({});
  const [actionHistoryModal, updateActionHistoryModal] = useState({ open: false, data: null });
  const [reportDownloading, setReportDownloading] = useState(false);
  const styledColumns = [RECEPTACLE_ID_HEADER, DESPATCH_ID_HEADER, CONSIGNMENT_ID_HEADER];
  const matchCommentPattern = str => /^[ -~\r\n]+$/.test(str);

  if (hasAccess([ROLE_ADMIN, ROLE_OPERATOR])) {
    tableColumns.splice(1, 0, TABLE_CARRIER_COLUMN);
    downloadColumns.splice(1, 0, DOWNLOAD_CARRIER_COLUMN);
  }

  if (hasAccess([ROLE_ADMIN, ROLE_CARRIER])) {
    tableColumns.splice(7, 0, TABLE_OPERATOR_COLUMN);
    downloadColumns.splice(7, 0, DOWNLOAD_OPERATOR_COLUMN);
  }

  const showFailure = failureReason => () => {
    updateFailure(failureReason);
  };
  const confirmStatusModalHandler = (items, status, comment) => {
    if (!matchCommentPattern(comment)) {
      newWarning('Comment should only contain ASCII characters (English letters, numbers and special characters)', 10);
    } else {
      bulkUpdateStatus(items, status, comment);
      updateStatusModal({});
    }
  };

  const confirmInvoiceNumberModalHandler = (items, invoiceNumber) => {
    if (invoiceNumber.length > 30) {
      newWarning('Invoice number length must be max 30 characters long', 10);
      updateInvoiceNumberModal({ ...invoiceNumberModal, open: true, data: invoiceNumber });
    } else if (!matchCommentPattern(invoiceNumber)) {
      newWarning('Comment should only contain ASCII characters (English letters, numbers and special characters)', 10);
      updateInvoiceNumberModal({ ...invoiceNumberModal, open: true, data: invoiceNumber });
    } else {
      bulkInvoiceNumberUpdate(items, invoiceNumber);
      updateInvoiceNumberModal({});
    }
  };
  const confirmDeleteInvoiceNumberModal = item => () => {
    deleteReceptaclesInvoiceNumber(item.id)();
    updateDeleteInvoiceModal({});
  };
  const dismissStatusModalHandler = () => {
    updateStatusModal({});
  };
  const dismissInvoiceNumberModalHandler = () => {
    updateInvoiceNumberModal({});
  };
  const dismissDeleteInvoiceNumberHandler = () => {
    updateDeleteInvoiceModal({});
  };
  const onChangeHandler = (e, values) => updateFromTo(values);
  const selectedStatusUpdate = status => () => {
    updateStatusModal({ open: true, status, items: selected });
  };
  const selectedInvoiceNumberUpdate = () => () => {
    updateInvoiceNumberModal({ open: true, items: selected });
  };
  const itemStatusUpdate = (item, status) => () => {
    updateStatusModal({ open: true, status, items: [item.id] });
  };
  const invoiceNumberUpdate = item => () => {
    updateInvoiceNumberModal({ open: true, data: item.invoiceNumber, items: [item.id] });
  };
  const deleteInvoiceNumber = item => () => {
    updateDeleteInvoiceModal({ open: true, item });
  };
  const showActionHistoryModal = item => () => {
    updateActionHistoryModal({ open: true, data: item.id });
  };
  const dismissActionHistoryModal = () => {
    updateActionHistoryModal({ open: false, data: null });
  };
  const toggleExtendData = () => updateExtended(!extended);
  const openAtatByReceptacleId = id => () => {
    window.open(`${process.env.atatUrl}/en/receptacle/${id}`, '_blank');
  };

  const actions = item => (
    <>
      {
        item.settlementStatus.indexOf('Approved') === -1
        && <div data-testid="approve-action" onClick={itemStatusUpdate(item, 'APPROVED')}>Approve</div>
      }
      {
        !!item.settlementStatus.indexOf('Disapproved')
        && <div data-testid="disapprove-action" onClick={itemStatusUpdate(item, 'DISAPPROVED')}>Disapprove</div>
      }
      <div data-testid="view-details" onClick={openAtatByReceptacleId(item.receptacleId)}>Receptacle Detail</div>
      {
        item.failureReason
        && <div data-testid="view-failure" onClick={showFailure(item.failureReason)}>Failure Reason</div>
      }
      {
        item.invoiceNumber
        && <div data-testid="editInvoice-action" onClick={invoiceNumberUpdate(item)}>Edit Invoice</div>
      }
      {
        !item.invoiceNumber
        && <div data-testid="addInvoice-action" onClick={invoiceNumberUpdate(item)}>Add Invoice</div>
      }
      {
        item.invoiceNumber
        && <div data-testid="deleteInvoice-action" onClick={deleteInvoiceNumber(item)}>Delete Invoice</div>
      }
      {
        <div data-testid="viewActionHistory-action" onClick={showActionHistoryModal(item)}>Show History</div>
      }
    </>
  );

  const filterData = (item) => {
    if (filter) {
      if (filter !== 'failed' && item.settlementStatus.toLowerCase().indexOf(filter) !== 0) {
        return false;
      }
      if (filter === 'failed' && item.alaLevel !== -1 && item.agreementAlaLevel !== -1) {
        return false;
      }
    }

    const agreementAlaLevel = (item.agreementAlaLevel === 'Fail' || item.agreementAlaLevel === null || item.alaLevel === -1) ? 10 : item.agreementAlaLevel;
    const alaLevel = (item.alaLevel === 'Fail' || item.alaLevel === null || item.alaLevel === -1) ? 10 : item.alaLevel;
    return ((alaLevel >= fromTo[0] && agreementAlaLevel >= fromTo[0]) && (alaLevel <= fromTo[1] || agreementAlaLevel <= fromTo[1]));
  };

  const onRowClick = (state, rowInfo, column) => {
    if (rowInfo && rowInfo.original && column) {
      switch (column.Header) {
      case RECEPTACLE_ID_HEADER: {
        window.open(`${process.env.atatUrl}/en/receptacle/${rowInfo.original.receptacleId}`, '_blank');
        break;
      }
      case DESPATCH_ID_HEADER: {
        window.open(`${process.env.atatUrl}/en/despatch/${rowInfo.original.despatchIdentifier}`, '_blank');
        break;
      }
      case CONSIGNMENT_ID_HEADER: {
        window.open(`${process.env.atatUrl}/en/consignment/${rowInfo.original.consignmentNr}`, '_blank');
        break;
      }
      default:
      }
    }
  };

  const downloadPassReport = async (event, reportType, reportFormat) => {
    setReportDownloading(true);
    newWarning('All receptacles with mandatory data from missing CARDIT (ALA 9 or Fail) shall not be included in the download '
      + 'as they shall block processing by external systems', 15);
    await request(`/pass-report/download?reportType=${reportType}&reportFormat=${reportFormat}`,
      {
        method: POST,
        responseType: 'blob',
        body: selected,
      });
    setReportDownloading(false);
  };

  return (
    <>
      <FailureReasonModal
        details={failure}
        onDismiss={updateFailure}
      />
      <ActionHistoryModal
        data={actionHistoryModal.data}
        onDismiss={dismissActionHistoryModal}
      />

      <UpdateStatusModal
        modal={statusModal}
        confirmHandler={confirmStatusModalHandler}
        dismissHandler={dismissStatusModalHandler}
      />

      <UpdateInvoiceNumberModal
        modal={invoiceNumberModal}
        confirmHandler={confirmInvoiceNumberModalHandler}
        dismissHandler={dismissInvoiceNumberModalHandler}
      />

      <DeleteInvoiceModal
        modal={deleteInvoiceModal}
        confirmHandler={confirmDeleteInvoiceNumberModal}
        dismissHandler={dismissDeleteInvoiceNumberHandler}
      />

      <AlaSlider onChange={onChangeHandler} />
      <TopButtonsWrapper>
        <Download columns={PASS_COLUMNS} fileName={FILE_NAME} title={TITLE} items={data} selected={selected} fontSize={4} />
        <Button onClick={selectedStatusUpdate('APPROVED')} disabled={!selected.length} data-testid="selected-approve">Approve</Button>
        <Button onClick={selectedStatusUpdate('DISAPPROVED')} disabled={!selected.length} data-testid="selected-disapprove">Disapprove</Button>
        <Button onClick={selectedInvoiceNumberUpdate()} disabled={!selected.length} data-testid="selected-add-invoice">Add invoice number</Button>
        <Button onClick={toggleExtendData} theme={redButton} data-testid="toggle-extended">{`${extended ? '- Basic' : '+ Expand'} receptacle data`}</Button>
        {(recordId
          ? <>
            <StyledLoader active={reportDownloading} spinner fadeSpeed={100} text="Generating Report...">
              <ButtonsList title="Download to output file" width="225px" overlap disabled={!selected.length || reportDownloading}>
                <div onClick={event => downloadPassReport(event, PASS_BILLING_DATA_REPORT, FILE_FORMAT_EXCEL)}>Excel</div>
                <div onClick={event => downloadPassReport(event, PASS_BILLING_DATA_REPORT, FILE_FORMAT_CSV)}>CSV</div>
                <div onClick={event => downloadPassReport(event, PASS_BILLING_DATA_REPORT, FILE_FORMAT_PDF)}>PDF</div>
              </ButtonsList>
              <ButtonsList title="Download reconciliation to output file" width="315px" overlap disabled={!selected.length || reportDownloading}>
                <div onClick={event => downloadPassReport(event, PASS_RECONCILIATION_DATA_REPORT, FILE_FORMAT_EXCEL)}>Excel</div>
                <div onClick={event => downloadPassReport(event, PASS_RECONCILIATION_DATA_REPORT, FILE_FORMAT_CSV)}>CSV</div>
                <div onClick={event => downloadPassReport(event, PASS_RECONCILIATION_DATA_REPORT, FILE_FORMAT_PDF)}>PDF</div>
              </ButtonsList>
            </StyledLoader>
            </>
          : (
            <Download
              items={data}
              selected={selected}
              buttonTitle="Download to output file"
              title={PASS_TITLE}
              columns={PASS_COLUMNS}
              fileName={PASS_FILE_NAME}
              fontSize={3}
              width="225px"
            />
          )
        )}
        <AlaTableView />
      </TopButtonsWrapper>

      <Table
        className="ReceptaclesList"
        columns={extended ? TABLE_COLUMNS_EXTENDED(tableColumns) : tableColumns}
        data={data.filter(filterData)}
        loading={loading}
        actions={actions}
        onSelect={updateSelected}
        styledColumns={styledColumns}
        onRowClick={onRowClick}
        elementCountLabel="receptacles"
        displayElementCount
      />
    </>
  );
}

RecordsListTable.propTypes = {
  recordId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  filter: PropTypes.string,
  search: PropTypes.string,
};

export default RecordsListTable;
